import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { filterMovies, movies, paginate, totalPages, type MediaType } from "@/lib/media";
import { categories, categoryDescription, sortOptions, typeLabels } from "@/mocks/site";
import FilterBar, { type CategoryFilterState } from "./components/FilterBar";

const PAGE_SIZE = 12;

const defaultFilter: CategoryFilterState = {
  region: "全部地区",
  year: "全部年份",
  genre: "全部类型",
  minRating: 0,
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const meta = categories.find((item) => item.key === type);
  const mediaType = (meta?.key ?? "movie") as MediaType;

  const [filter, setFilter] = useState<CategoryFilterState>(defaultFilter);
  const [sort, setSort] = useState<"heat" | "newest" | "rating">("heat");
  const [page, setPage] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilter(defaultFilter);
    setSort("heat");
    setPage(1);
  }, [type]);

  const genres = useMemo(() => {
    const pool = movies.filter((item) => item.type === mediaType);
    const unique = Array.from(new Set(pool.flatMap((item) => item.genres)));
    return ["全部类型", ...unique];
  }, [mediaType]);

  const result = useMemo(
    () => filterMovies({ type: mediaType, ...filter, sort }),
    [mediaType, filter, sort],
  );

  const maxPage = totalPages(result.length, PAGE_SIZE);
  const currentPage = Math.min(page, maxPage);
  const pageItems = paginate(result, currentPage, PAGE_SIZE);
  const averageRating = result.length
    ? (result.reduce((sum, item) => sum + item.rating, 0) / result.length).toFixed(1)
    : "—";

  useDocumentMeta({
    title: `${meta?.name ?? "影视分类"} - 色虎影视分类`,
    description: meta
      ? `${categoryDescription[mediaType]}色虎${meta.name}频道收录 ${movies.filter((item) => item.type === mediaType).length} 部演示作品，支持地区、年份、类型与评分筛选。`
      : "色虎影视分类页面，提供电影、电视剧、综艺与动画的分类筛选与热门推荐。",
    keywords: `色虎,${meta?.name ?? "影视"}分类,${typeLabels[mediaType]}推荐,影视筛选,热门榜`,
  });

  const updateFilter = (next: Partial<CategoryFilterState>) => {
    setFilter((current) => ({ ...current, ...next }));
    setPage(1);
  };

  const resetFilter = () => {
    setFilter(defaultFilter);
    setPage(1);
  };

  const scrollToList = () => {
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!meta) {
    return (
      <SiteLayout>
        <div className="portal-container py-16">
          <EmptyState
            icon="ri-folder-warning-line"
            title="没有找到这个分类"
            description="色虎目前提供电影、电视剧、综艺与动画四个分类，可以返回首页重新选择。"
            actionLabel="返回首页"
            onAction={() => window.REACT_APP_NAVIGATE("/")}
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="bg-primary-900 pb-14 pt-6">
        <div className="portal-container">
          <Breadcrumb tone="dark" items={[{ label: "影视分类", to: "/" }, { label: meta.name }]} />
          <div className="mt-5 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-accent-300">
                <i className={`${meta.icon} text-base`} aria-hidden="true" />
                {meta.english} Channel
              </p>
              <h1 className="mt-2 flex flex-wrap items-center gap-3 font-heading text-3xl font-black text-background-50 md:text-4xl">
                {meta.name}
                <span className="rounded-md bg-accent-500 px-2 py-1 text-xs font-semibold text-primary-950">分类</span>
              </h1>
              <p className="mt-3 text-sm leading-7 text-primary-200">{categoryDescription[mediaType]}</p>
            </div>
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              <div className="rounded-lg border border-primary-700 bg-primary-800/60 px-4 py-3">
                <p className="font-heading text-xl font-bold text-background-50">{movies.filter((item) => item.type === mediaType).length}</p>
                <p className="mt-1 text-[11px] text-primary-300">收录作品</p>
              </div>
              <div className="rounded-lg border border-primary-700 bg-primary-800/60 px-4 py-3">
                <p className="font-heading text-xl font-bold text-accent-300">{averageRating}</p>
                <p className="mt-1 text-[11px] text-primary-300">当前均分</p>
              </div>
              <div className="rounded-lg border border-primary-700 bg-primary-800/60 px-4 py-3">
                <p className="font-heading text-xl font-bold text-background-50">{result.length}</p>
                <p className="mt-1 text-[11px] text-primary-300">筛选结果</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {categories.map((item) => (
              <Link
                key={item.key}
                to={`/category/${item.key}`}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                  item.key === meta.key
                    ? "bg-accent-500 font-semibold text-primary-950"
                    : "border border-primary-700 text-primary-200 hover:border-accent-400 hover:text-accent-300"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="portal-container -mt-8 scroll-mt-32 pb-4" ref={listRef}>
        <div className="rounded-lg border border-background-200 bg-background-50 p-3 md:p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共 <span className="font-semibold text-primary-700">{result.length}</span> 部{meta.name}作品
              <span className="ml-2 hidden text-xs text-foreground-400 sm:inline">
                当前为演示数据，筛选与排序均在前端完成
              </span>
            </p>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSort(option.key);
                    setPage(1);
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-1 text-xs transition-colors ${
                    sort === option.key
                      ? "bg-background-50 font-medium text-primary-700 shadow-none"
                      : "text-foreground-500 hover:text-primary-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <FilterBar filter={filter} onChange={updateFilter} onReset={resetFilter} availableGenres={genres} />

          <div className="mt-5">
            {pageItems.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-search-eye-line"
                title="没有符合当前条件的作品"
                description="试着放宽年份或评分条件，也可以重置筛选看看全部推荐。"
                actionLabel="重置筛选条件"
                onAction={resetFilter}
              />
            )}
          </div>

          {pageItems.length > 0 && (
            <Pagination
              page={currentPage}
              total={maxPage}
              onChange={(next) => {
                setPage(next);
                scrollToList();
              }}
            />
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <p className="text-xs text-foreground-500">
            其他分类：
            {categories
              .filter((item) => item.key !== meta.key)
              .map((item) => (
                <Link
                  key={item.key}
                  to={`/category/${item.key}`}
                  className="ml-2 text-primary-700 hover:text-accent-600"
                >
                  {item.name}
                </Link>
              ))}
          </p>
          <Link to="/news" className="text-xs font-medium text-primary-700 hover:text-accent-600">
            查看相关影视资讯
            <i className="ri-arrow-right-line ml-1" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}